import { useSnackbar } from 'notistack';
import { useCallback, useEffect } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useEmployeeLoginData } from '~/data/employee';
import { Roles } from '~/data/user';

import { putEmpRoles } from './data';

export type IFormInput = {
  fUserRoles?: string[];
};

export type useEmployeeDataOutput = {
  handleRolesChange: (employeeRoles: Roles[]) => void;
  onSubmit: () => void;
  employeeRoles: Roles[];
};

export const useChangeRole = (props: {
  onSubmitSuccess?: () => void;
}): useEmployeeDataOutput => {
  const { onSubmitSuccess } = props;
  const { t } = useTranslation();
  const {
    employeeAdminLoginData: employeeLoginData,
    loginDataLoading: isLoading,
    loginDataFailedLoading: failedToLoad,
  } = useEmployeeLoginData();

  const [employeeRoles, setEmployeeRoles] = useState<Roles[]>([]);
  const [defaultRoles, setDefaultRoles] = useState<Roles[]>([]);

  useEffect(() => {
    if (
      !isLoading &&
      !failedToLoad &&
      Object.keys(employeeLoginData).length > 0
    ) {
      const initialRoles = Object.values(Roles).filter((r) =>
        employeeLoginData.fLoginRoles.includes(r),
      );
      setEmployeeRoles(initialRoles);
      setDefaultRoles(initialRoles);
    }
  }, [employeeLoginData, failedToLoad, isLoading]);

  const { enqueueSnackbar } = useSnackbar();

  const handleRolesChange = useCallback(
    (updatedEmpRoles: Roles[]) => {
      setEmployeeRoles(updatedEmpRoles);
    },
    [setEmployeeRoles],
  );

  const handleFormSubmit = (postData: string[]) => {
    (async () => {
      try {
        const response = await putEmpRoles(
          employeeLoginData.fLoginUsername,
          postData,
        );
        if (response) {
          if (!!onSubmitSuccess) {
            onSubmitSuccess();
          }
          enqueueSnackbar(t('change_saved'), {
            variant: 'success',
          });
        }
      } catch (error) {
        enqueueSnackbar(t('laErrorRemoveRoleResponsibilities'), {
          variant: 'error',
        });
        setEmployeeRoles(defaultRoles);
      }
    })();
  };

  const onSubmit = () => {
    handleFormSubmit(employeeRoles);
  };

  return {
    employeeRoles,
    handleRolesChange,
    onSubmit,
  };
};
